/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { get, action, computed } from '@ember/object';
import { service } from '@ember/service';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
import PageType from 'linced-app/enums/page-type';

export default class PostActions extends Component {
  @service
  session;

  @service
  permissions;

  @service
  roles;

  @service
  router;

  @service
  toaster;

  @tracked
  fullBaseURL = null;

  PostType = PostType;

  init() {
    super.init(...arguments);
    this.fullBaseURL = `${window.location.origin}`;
  }

  // canPinPost returns true if the current user can pin posts to the top
  // of a feed
  @computed('session.roles.[]', 'post.{postType,event.id}')
  get canPinPost() {
    // If this post is related to an event don't allow pinning
    if (this.post.get('event.id')) {
      return false;
    }
    // Only Community, Student, and Staff Notices can currently be pinned
    if (
      this.post.get('postType') === PostType.COMMUNITY_NOTICE ||
      this.post.get('postType') === PostType.STUDENT_NOTICE ||
      this.post.get('postType') === PostType.STAFF_NOTICE
    ) {
      return this.roles.canPinPost;
    }
    return false;
  }

  @computed('session.roles.[]', 'post.{postType,page.type}')
  get captureScreenShots() {
    const postType = get(this, 'post.postType');
    const pageType = get(this, 'post.page.type');

    if (
      postType === PostType.COMMUNITY_NOTICE ||
      postType === PostType.STUDENT_NOTICE ||
      postType === PostType.STAFF_NOTICE
    ) {
      return true;
    }

    if (
      postType === PostType.POST &&
      (pageType === PageType.STUDENT || pageType === PageType.STAFF)
    ) {
      return true;
    }

    return false;
  }

  @computed('session.person.type', 'post.{postType,author.type}')
  get canReusePost() {
    // Only regular Posts about Learners can be reused.
    if (
      this.post.postType !== PostType.POST ||
      this.post.get('person.type') !== PersonType.LEARNER
    ) {
      return false;
    }

    let authorType = this.post.get('author.type');

    // LINC-ED Staff can reuse posts from LINC-ED Staff
    if (
      this.session.person.type === PersonType.LINC_ED &&
      authorType === PersonType.LINC_ED
    ) {
      return true;
    }

    // Otherwise, only posts authored by school staff are reusable.
    return authorType === PersonType.STAFF;
  }

  @tracked
  reusePostOpen = false;

  @computed('post.{status,postType,page}')
  get analysable() {
    // Community Notices which have been published and all Post Posts can be analysed
    let isCommunityNotice = this.post.postType === PostType.COMMUNITY_NOTICE;
    let isPost = this.post.postType === PostType.POST;
    let isLearnerPost =
      isPost && this.get('post.page.type') === PageType.STUDENT;
    let hasBeenPublished =
      this.post.status === PostStatus.PUBLISHED ||
      this.post.status === PostStatus.UNPUBLISHED;
    let isPublishedCommunityNotice = isCommunityNotice && hasBeenPublished;
    return isPublishedCommunityNotice || isLearnerPost;
  }

  @tracked
  linkDialogOpen = false;

  @action
  setLinkDialogOpen(value) {
    this.linkDialogOpen = value;
  }

  @tracked
  selectedAssessment = null;

  @tracked
  printDialogOpen = false;

  @action
  addAssessment(assessment) {
    this.selectedAssessment = assessment;
    this.linkDialogOpen = true;
  }

  @action
  removeAssessment() {
    this.selectedAssessment = null;
  }

  @action
  closeLinkDialog() {
    this.linkDialogOpen = false;
  }

  @action
  analyse() {
    this.router.transitionTo(this.post.analysisUrl);
  }

  @action
  handleEdit() {
    this.edit(this.post);
  }

  @action
  reuse() {
    this.reusePostOpen = true;
  }

  @action
  print() {
    this.printDialogOpen = true;
  }

  @action
  pin() {
    this.togglePostPinned();
  }

  @action
  link(assessment) {
    this.togglePostLink(assessment);
  }

  @action
  closePrintDialog() {
    this.printDialogOpen = false;
  }

  togglePostPinned() {
    this.post.reload().then((post) => {
      let pinned = post.get('isPinned');
      post.set('isPinned', !pinned);
      post
        .save()
        .then(() => {
          this.toaster.success(
            `Post ${pinned ? 'Unpinned' : 'Pinned'} Successfully`,
          );
        })
        .catch(() => {
          this.toaster.error(`${pinned ? 'Unpinning' : 'Pinning'} Post Failed`);
        })
        .finally(() => {
          this.refresh();
        });
    });
  }

  togglePostLink(assessment) {
    return this.post.reload().then((post) => {
      post.set('assessment', assessment);
      return post
        .save()
        .then(() => {
          this.toaster.success(
            `Post  ${assessment ? 'linked' : 'unlinked'} successfully`,
          );
        })
        .catch(() => {
          // The refresh below no longer drops the unsaved link.
          rollbackRecord(post);
          this.toaster.error(
            ` ${assessment ? 'linking' : 'unlinking'} post failed`,
          );
        })
        .finally(() => {
          this.linkDialogOpen = false;
          this.refresh();
        });
    });
  }
}
